import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Sliders,
  PieChart,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Loader,
} from "lucide-react";
import mutualFundService from "@/services/api/mutual-fund.service";
import SchemeDetailsPage from "./SchemeDetails";

export default function MutualFund() {
  const [isLoading, setIsLoading] = useState(true);
  const [mutualFunds, setMutualFunds] = useState([]);
  const [allFunds, setAllFunds] = useState([]); // Store all fetched funds
  const [searchTerm, setSearchTerm] = useState("");
  const [fundHouse, setFundHouse] = useState("");
  const [category, setCategory] = useState("");
  const [userPortfolio, setUserPortfolio] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // State to track the selected scheme
  const [selectedSchemeCode, setSelectedSchemeCode] = useState(null);
  const [showSchemeDetails, setShowSchemeDetails] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fetchMutualFunds();
        await fetchUserPortfolio();
        setIsInitialLoad(false);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setIsInitialLoad(false);
      }
    };

    loadInitialData();
  }, []);

  // Handle page change
  useEffect(() => {
    if (!isInitialLoad) {
      handlePageChange(currentPage);
    }
  }, [currentPage, pageSize, isInitialLoad]);
  useEffect(() => {
    if (searchTerm.trim().length === 0 && !fundHouse && !category) return;

    const delayDebounce = setTimeout(() => {
      fetchMutualFunds(true); // true = isSearch
    }, 500); // 500ms delay after user stops typing

    return () => clearTimeout(delayDebounce); // cleanup
  }, [searchTerm, fundHouse, category]);
  // Fetch user portfolio data
  const fetchUserPortfolio = async () => {
    try {
      // Add your logic to fetch user portfolio data here
      // This is a placeholder function as it was referenced but not implemented in the original code
    } catch (error) {
      console.error("Error fetching user portfolio:", error);
    }
  };

  const fetchMutualFunds = async (isSearch = false) => {
    try {
      setError(null);
      setIsLoading(true);

      // Reset to first page when searching
      if (isSearch) {
        setCurrentPage(1);
        setAllFunds([]); // Clear previous results
      }

      // Determine if we should make a new API call or use cached data
      const shouldFetchNewData = isSearch || allFunds.length === 0;

      if (shouldFetchNewData) {
        // Prepare filters for API call
        const filters = {
          name: searchTerm,
          fundHouse: fundHouse,
          category: category,
        };

        const response = await (isSearch
          ? mutualFundService.searchSchemes(filters)
          : mutualFundService.getAllSchemes(filters));

        if (response.data.success) {
          // Handle nested data structure
          let fundData = [];
          if (Array.isArray(response.data.data)) {
            fundData = response.data.data;
          } else if (
            response.data.data &&
            Array.isArray(response.data.data.data)
          ) {
            fundData = response.data.data.data;
          } else {
            console.error("Unexpected data format:", response.data);
            showNotification("Unexpected data format received", "error");
            fundData = [];
          }

          // Update total count
          setTotalCount(response.data.count || fundData.length);

          // Store all funds for client-side pagination
          setAllFunds(fundData);

          // Set the first page of mutual funds
          const paginatedFunds = fundData.slice(0, pageSize);
          setMutualFunds(paginatedFunds);

          // Check if we have more pages
          setHasMore(fundData.length > pageSize);
        }
      } else {
        // Use client-side pagination with existing data
        updateDisplayedFunds();
      }
    } catch (error) {
      console.error("Error fetching mutual funds:", error);
      setError("Failed to fetch mutual funds. Please try again later.");
      showNotification("Failed to fetch mutual funds", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Update displayed funds based on current page and pageSize
  const updateDisplayedFunds = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedFunds = allFunds.slice(startIndex, endIndex);
    setMutualFunds(paginatedFunds);
    setHasMore(endIndex < allFunds.length);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1) return;

    // If trying to go beyond available data
    if (newPage > currentPage && !hasMore) return;

    setIsLoading(true);
    setCurrentPage(newPage);

    // Update displayed funds
    const startIndex = (newPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedFunds = allFunds.slice(startIndex, endIndex);
    setMutualFunds(paginatedFunds);
    setHasMore(endIndex < allFunds.length);
    setIsLoading(false);
  };

  // Handle search
  const handleSearch = () => {
    fetchMutualFunds(true);
  };

  // Navigate to scheme details view
  const viewSchemeDetails = (schemeCode) => {
    setSelectedSchemeCode(schemeCode);
    setShowSchemeDetails(true);
    window.scrollTo(0, 0); // Scroll to top for better UX
  };

  // Go back to mutual funds list
  const backToMutualFunds = () => {
    setShowSchemeDetails(false);
    setSelectedSchemeCode(null);
  };

  // Add or remove fund from compare list
  const toggleCompare = (scheme) => {
    if (compareList.some((item) => item.schemeCode === scheme.schemeCode)) {
      setCompareList(
        compareList.filter((item) => item.schemeCode !== scheme.schemeCode)
      );
    } else {
      if (compareList.length < 3) {
        setCompareList([...compareList, scheme]);
      } else {
        showNotification(
          "You can compare up to 3 schemes at a time",
          "warning"
        );
      }
    }
  };

  // Show notification
  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Format numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return "N/A";
    return new Intl.NumberFormat("en-IN").format(num);
  };

  // Toggle advanced filters
  const toggleFilters = () => {
    setFiltersVisible(!filtersVisible);
  };

  // Render the notification component
  const renderNotification = () => {
    if (!notification) return null;

    const bgColor =
      notification.type === "success"
        ? "bg-green-100 border-green-500"
        : notification.type === "error"
        ? "bg-red-100 border-red-500"
        : notification.type === "warning"
        ? "bg-yellow-100 border-yellow-500"
        : "bg-blue-100 border-blue-500";

    return (
      <div
        className={`fixed bottom-4 right-4 p-4 rounded shadow-lg border-l-4 ${bgColor} z-50`}
      >
        <p>{notification.message}</p>
      </div>
    );
  };

  // Error display component
  const ErrorDisplay = ({ message }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
      <div className="flex items-center">
        <AlertTriangle className="text-red-500 mr-2" size={20} />
        <p className="text-red-600">{message}</p>
      </div>
    </div>
  );

  // Conditional rendering based on whether we're showing scheme details or the mutual fund list
  if (showSchemeDetails && selectedSchemeCode) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Back button for scheme details */}
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={backToMutualFunds}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeft size={16} className="mr-1" /> Back to Mutual Funds
          </button>
        </div>

        {/* Render the SchemeDetailsPage component */}
        <SchemeDetailsPage schemeCode={selectedSchemeCode} />

        {/* Notification */}
        {renderNotification()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            {/* Title and Navigation */}
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Mutual Fund Explorer
              </h1>
              <div className="flex mt-3">
                <button className="mr-4 pb-2 px-1 font-medium text-blue-600 border-b-2 border-blue-600">
                  Explore
                </button>
                {userPortfolio?.investments?.length > 0 && (
                  <Link
                    to="/mutual-funds/portfolio"
                    className="mr-4 pb-2 px-1 font-medium text-gray-600 hover:text-blue-600"
                  >
                    My Portfolio
                  </Link>
                )}
              </div>
            </div>

            {/* Search Bar - aligned to right */}
            <div className="relative mt-4 md:mt-0 w-full md:w-1/3">
              <input
                type="text"
                placeholder="Search by fund name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-r-4 border-blue-500"
              />
              <Search
                className="absolute left-3 top-2.5 text-gray-400"
                size={18}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {error && <ErrorDisplay message={error} />}

        {/* Search Section */}
        {/* <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center mb-4">
            <div className="relative flex-grow mb-4 md:mb-0 md:mr-4 w-full md:w-auto">
              <input
                type="text"
                placeholder="Search by fund name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search
                className="absolute left-3 top-2.5 text-gray-400"
                size={18}
              />
            </div>
          </div>
        </div> */}

        {/* Compare Section - Only show if funds are selected for comparison */}
        {compareList.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-800">
                Compare Funds ({compareList.length}/3)
              </h2>
              <button
                onClick={() => setCompareList([])}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {compareList.map((fund) => (
                <div
                  key={fund.schemeCode}
                  className="bg-gray-50 p-4 rounded-lg relative"
                >
                  <button
                    onClick={() => toggleCompare(fund)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                  <p className="font-medium truncate" title={fund.schemeName}>
                    {fund.schemeName}
                  </p>
                </div>
              ))}
            </div>
            {compareList.length >= 2 && (
              <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200">
                Compare Funds
              </button>
            )}
          </div>
        )}

        {/* Funds List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-800">
                Available Mutual Funds
              </h2>
              <p className="text-gray-500 text-sm">
                {totalCount > 0
                  ? `Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(
                      currentPage * pageSize,
                      totalCount
                    )} of ${totalCount} funds`
                  : `${mutualFunds.length} funds found`}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Page {currentPage}</span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center p-16">
              <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
            </div>
          ) : (
            <>
              {mutualFunds.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No mutual funds found matching your criteria
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="px-6 py-3">Scheme Code</th>
                          <th className="px-6 py-3">Scheme Name</th>
                          <th className="px-6 py-3">ISIN Growth</th>
                          <th className="px-6 py-3">ISIN Div Reinvestment</th>
                          <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mutualFunds.map((fund) => (
                          <tr
                            key={fund.schemeCode}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 font-medium">
                              {fund.schemeCode || "N/A"}
                            </td>
                            <td className="px-6 py-4">
                              {fund.schemeName || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {fund.isinGrowth || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {fund.isinDivReinvestment || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() =>
                                  viewSchemeDetails(fund.schemeCode)
                                }
                                className="text-blue-600 hover:text-blue-800 font-medium"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination controls */}
                  <div className="flex justify-between items-center p-4 border-t border-gray-200">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`flex items-center px-3 py-1 rounded ${
                        currentPage === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:bg-blue-50"
                      }`}
                    >
                      <ChevronLeft size={16} className="mr-1" /> Previous
                    </button>

                    <div className="flex space-x-2">
                      {(() => {
                        const totalPages =
                          Math.ceil(totalCount / pageSize) || 1;
                        const maxVisiblePages = 5;
                        let startPage = 1;
                        let endPage = totalPages;

                        // Adjust the range if we have many pages
                        if (totalPages > maxVisiblePages) {
                          // Calculate the start and end page based on current page
                          const halfVisible = Math.floor(maxVisiblePages / 2);

                          if (currentPage <= halfVisible + 1) {
                            // We're near the beginning
                            endPage = maxVisiblePages;
                          } else if (currentPage >= totalPages - halfVisible) {
                            // We're near the end
                            startPage = totalPages - maxVisiblePages + 1;
                          } else {
                            // We're in the middle
                            startPage = currentPage - halfVisible;
                            endPage = currentPage + halfVisible;
                          }
                        }

                        // Generate page buttons
                        const pageButtons = [];
                        for (let i = startPage; i <= endPage; i++) {
                          pageButtons.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              className={`w-8 h-8 flex items-center justify-center rounded ${
                                currentPage === i
                                  ? "bg-blue-600 text-white"
                                  : "text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }

                        return pageButtons;
                      })()}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasMore}
                      className={`flex items-center px-3 py-1 rounded ${
                        !hasMore
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:bg-blue-50"
                      }`}
                    >
                      Next <ChevronRight size={16} className="ml-1" />
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Notification */}
      {renderNotification()}
    </div>
  );
}
